import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

const navigation = [
  { name: "Nosotros", href: "#nosotros" },
  { name: "Servicios", href: "#servicios" },
  { name: "Equipo", href: "#equipo" },
  { name: "Nuestros Clientes", href: "#nuestros-clientes" },
  { name: "Insights", href: "/insights" },
  { name: "Contacto", href: "/contacto" },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleNavClick = (href: string) => {
    setIsOpen(false);
    if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        window.location.href = "/" + href;
        return;
      }
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          isScrolled
            ? "bg-navy-deep/95 backdrop-blur-lg shadow-lg py-2"
            : "bg-transparent py-5"
        )}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex flex-col items-center gap-0 leading-none">
              <span className="font-display text-2xl font-bold tracking-[0.15em] text-bone">
                SYCMA
              </span>
              <div className="w-full h-px bg-gold my-0.5" />
              <span className="font-display text-[9px] tracking-[0.25em] uppercase text-bone/70">
                Corporate Finance
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-7">
              {navigation.map((item) =>
                item.href.startsWith("/") ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="font-sans text-sm font-medium text-bone/80 hover:text-gold transition-colors duration-200"
                  >
                    {item.name}
                  </Link>
                ) : (
                  <button
                    key={item.name}
                    onClick={() => handleNavClick(item.href)}
                    className="font-sans text-sm font-medium text-bone/80 hover:text-gold transition-colors duration-200"
                  >
                    {item.name}
                  </button>
                )
              )}
            </nav>

            {/* CTA */}
            <div className="hidden lg:block">
              <Link
                to="/contacto"
                className="btn-gold text-sm px-6 py-3 rounded-lg"
              >
                Agendar diagnóstico
              </Link>
            </div>

            {/* Mobile Menu */}
            <button
              className="lg:hidden p-2 z-[60] relative"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="h-6 w-6 text-bone" />
              ) : (
                <Menu className="h-6 w-6 text-bone" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Overlay */}
      <div
        className={cn(
          "fixed inset-0 z-[55] lg:hidden transition-all duration-300",
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      >
        <div className="absolute inset-0 bg-black/50" onClick={() => setIsOpen(false)} />
        <div
          className={cn(
            "absolute top-0 left-0 right-0 bg-navy-deep shadow-xl transition-transform duration-300 max-h-[90vh] overflow-y-auto",
            isOpen ? "translate-y-0" : "-translate-y-full"
          )}
        >
          <div className="px-4 pt-20 pb-8">
            <nav className="space-y-1">
              {navigation.map((item) => (
                <div key={item.name} className="border-b border-border/30">
                  {item.href.startsWith("/") ? (
                    <Link
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className="block py-4 font-sans text-base font-medium text-bone/80 hover:text-gold transition-colors"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleNavClick(item.href)}
                      className="block w-full text-left py-4 font-sans text-base font-medium text-bone/80 hover:text-gold transition-colors"
                    >
                      {item.name}
                    </button>
                  )}
                </div>
              ))}
            </nav>
            <div className="mt-6">
              <Link
                to="/contacto"
                onClick={() => setIsOpen(false)}
                className="btn-gold block text-center w-full text-base px-6 py-4 rounded-lg"
              >
                Agendar diagnóstico
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
